import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useForm } from 'react-hook-form'

import { Box, Checkbox, Stack, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Button, Card } from '@repo/ui'
import { NARROW_COLUMN_WIDTH } from 'config'
import { QueryErrorState } from 'components/feedback'
import { Header } from 'components/navigation'
import { Search } from 'components/input/Search'
import { RichTextEditorField, SelectField, TextField } from 'components/input/fields'
import { COMPANY_ROLE_NAMES, ROLE_DISPLAY_NAMES } from 'constants/roles'
import {
  announcementBodyHasText,
  isRichTextBody,
  plainTextBodyToHtml,
} from 'utils/announcementBody'
import {
  useCreateAnnouncement,
  useInternalAnnouncement,
  useInternalAnnouncementBoardItems,
  useUpdateAnnouncement,
} from 'hooks/api'
import { ROUTES } from 'routes'
import { ANNOUNCEMENT_KINDS, ANNOUNCEMENT_KIND_LABELS } from '@repo/types'
import type { AnnouncementKind } from 'services/announcementsApi'

import { InternalAnnouncementEditPageSkeleton } from './InternalAnnouncementEditPageSkeleton'

/**
 * Write or edit one announcement (VRG-221 / VRG-332, staff-only).
 *
 * ## Why this is a PAGE and no longer a dialog
 *
 * It was an `AnnouncementEditorModal` and had outgrown one. The form is title, body,
 * kind, audience, a CTA label, a CTA route, a tour key and a searchable multi-select of
 * every linkable feature request — nine controls, one of them a scrolling list, stacked
 * inside a dialog that scrolled independently over the table it was editing. Two things
 * a dialog cannot give that this form specifically needs: an ADDRESS (a half-written
 * draft could not be reloaded, linked to a colleague, or reopened after a refresh), and
 * room to group related fields into sections rather than one undifferentiated column.
 *
 * ## Create and edit are ONE component
 *
 * `page` switches the title, the submit verb and which mutation runs. NOTHING else
 * branches on it. This is `ProductEditPage`'s pattern and its reasoning verbatim (§30):
 * a forked "create" copy is how the two drift, and every field added later would have to
 * be added twice — which is precisely the failure this surface is at risk of, because
 * fields keep being added to it.
 *
 * ## Back-navigation is the BREADCRUMB (§42)
 *
 * There is no "← Back" button here on purpose. The list is this page's breadcrumb
 * parent, and for a hierarchical drill-in the breadcrumb IS the back affordance —
 * `ReferrerBackLink` self-suppresses on exactly this case so the two never appear
 * together. See the route comment in `routes.ts` for why the edit route is `/:id` rather
 * than `/:id/edit`: it is what makes that trail render a title instead of a bare uuid.
 *
 * ## Draft/publish semantics are UNCHANGED
 *
 * Creating saves a DRAFT and nothing else; publishing is a separate act, taken from the
 * list's row menu. A create call that also broadcasts is a create call you cannot
 * review, and moving the form onto a page is not a reason to revisit that.
 */
interface Props {
  page: 'create' | 'edit'
}

/**
 * The centred single-column measure, wrapping the WHOLE page — `Header` included.
 *
 * Header-inside is the load-bearing part and is not obvious: `WhatsNewPage` capped only
 * its content, which left the title hard against the page's left edge while the cards
 * sat centred hundreds of pixels away, reading as two unrelated things. A centred column
 * owns its heading. `InvoicePage` and `InternalSupportRequestPage` both wrap the header,
 * and this copies them — including wrapping the loading and error branches, so a
 * full-width skeleton never resolves into an 1100px page (the §19 layout shift a
 * skeleton exists to prevent; InvoicePage hit exactly that).
 *
 * This is a form — labelled fields and a body — so it is the "the fields just stretch"
 * case the cap is for. The LIST at `/internal/announcements` is deliberately NOT capped:
 * it renders a data grid, and capping a page that embeds one is measured harm rather
 * than a preference (simulating this cap on `RolePage`'s members grid produced 692px of
 * horizontal overflow, hiding the email and actions columns). Forms and prose get the
 * cap, data tables do not — the rule `SettingsPage`'s `narrow` flag already encodes.
 */
const Column = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>{children}</Box>
)

/**
 * DERIVED from the shared registry, not re-typed. `ANNOUNCEMENT_KINDS` fixes the
 * order and `ANNOUNCEMENT_KIND_LABELS` the wording, so adding a kind is one edit in
 * `@repo/types` rather than three that can disagree — which is exactly what nearly
 * happened when `security` and `deprecation` were added and this list, the card's
 * private label map, and the shared map were all separate copies.
 *
 * The picker shows the READER-FACING label ("Feature", "Deprecated"), because the author
 * is choosing what the brand will see, and an internal-only synonym here would mean
 * picking "Fix" and shipping a chip that says something else.
 */
const KIND_OPTIONS: { label: string; value: AnnouncementKind }[] = ANNOUNCEMENT_KINDS.map(
  kind => ({ label: ANNOUNCEMENT_KIND_LABELS[kind], value: kind }),
)

/**
 * The audience gate's options: the built-in COMPANY roles, by name (VRG-332).
 *
 * ## Why roles and not permissions
 *
 * The gate used to be a single PERMISSION name, and it asked the wrong question in the
 * wrong vocabulary. "Who should see this?" is answered on the roles screen — Admin,
 * Finance, Design — not in a ~110-entry permission catalog, and the two do not line up:
 * gating a billing announcement on `billing_view` hides it from every company that
 * granted billing to a role Vera has never heard of, and shows it to whoever a company
 * granted that one permission to for an unrelated reason.
 *
 * ## Names, and the limitation that comes with them
 *
 * The value is the role NAME, matched against the reader's own. Built-in names resolve
 * identically across every company (a per-company permission OVERRIDE keeps the built-in
 * name), so `company_finance` reaches every brand's Finance including one that rewrote
 * what Finance can do. A company that authored its own STANDALONE role — "Bookkeeper"
 * rather than Finance — will not match, and its people will not see the announcement.
 * That is a real gap and it is accepted rather than hidden: matching on permissions held
 * is the axis this is moving away from, and there is no honest way to match on "roles
 * that look similar".
 *
 * `ROLE_DISPLAY_NAMES` supplies the labels rather than a list re-typed here, so a role
 * reads the same word in this picker as it does in the role editor and the Users table.
 */
const ROLE_OPTIONS = COMPANY_ROLE_NAMES.map(name => ({
  label: ROLE_DISPLAY_NAMES[name] ?? name,
  value: name,
}))

export const InternalAnnouncementEditPage = ({ page }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const isEdit = page === 'edit'

  /**
   * The platform `TextField` destructures `formState: { errors }` straight out of its
   * props — `FieldProps extends UseFormReturn` — so a caller that omits the form
   * plumbing does not degrade, it THROWS on render ("Cannot read properties of
   * undefined (reading 'errors')"), taking the whole surface down with it.
   *
   * This page keeps its values in `useState` below; react-hook-form is here only to
   * satisfy that contract, exactly as `OwnerDetailsFields` does (`{...methods}` beside
   * a controlled `value` / `onValueChange`). Switching wholesale onto RHF would be the
   * larger, more invasive change and buys nothing here — the one validation rule this
   * form has (`routeInvalid`) is already computed from state.
   */
  const formMethods = useForm()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [kind, setKind] = useState<AnnouncementKind>('feature')
  const [ctaLabel, setCtaLabel] = useState('')
  const [ctaRoute, setCtaRoute] = useState('')
  const [tourKey, setTourKey] = useState('')
  const [roleNames, setRoleNames] = useState<string[]>([])
  /**
   * The LEGACY permission gate, if this row carries one. Read-only — the form never
   * sets it, it only offers to clear it. Kept in state rather than read straight off
   * `announcement` so clearing it takes effect on screen before the save round-trips.
   */
  const [legacyPermission, setLegacyPermission] = useState<string | null>(null)
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set())
  const [itemSearch, setItemSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  /**
   * isDirty baseline (VRG-364). This form keeps its values in `useState` rather
   * than react-hook-form (see the comment on `formMethods` above), so there is
   * no `formState.isDirty` to read — this ref stands in for it: it is the
   * loaded record's own values, updated by the seeding effect below, and Save
   * is disabled in edit mode until the live state diverges from it. Starts as
   * the same empty defaults the `useState` calls above use, which is correct
   * for `create` (nothing loaded to diverge from — see `isEdit &&` below).
   */
  const initialValuesRef = useRef({
    title: '',
    body: '',
    kind: 'feature' as AnnouncementKind,
    ctaLabel: '',
    ctaRoute: '',
    tourKey: '',
    roleNames: [] as string[],
    legacyPermission: null as string | null,
    linkedIds: new Set<string>(),
  })

  const {
    data: announcement,
    isLoading: loadingAnnouncement,
    isError,
    error: loadError,
    refetch,
  } = useInternalAnnouncement(isEdit ? id : undefined)

  const { data: boardItems = [], isLoading: itemsLoading } = useInternalAnnouncementBoardItems()
  const create = useCreateAnnouncement()
  const update = useUpdateAnnouncement()

  /**
   * Seed the form once the record arrives.
   *
   * Keyed on the id rather than the whole object: react-query hands back a NEW object
   * identity on every background refetch, and depending on that would re-seed the form
   * from the server mid-edit and silently discard whatever the staffer had typed. The
   * modal this replaces got the same protection from its `open` dependency; a page has
   * no open/closed edge, so the id is what stands in for it.
   */
  useEffect(() => {
    if (!announcement) return
    // A LEGACY plain-text body is converted to paragraph markup before it reaches the
    // editor, and this is data-loss prevention rather than tidiness: tiptap parses
    // whatever string it is handed as HTML, and HTML collapses newlines to spaces. Seeded
    // raw, every paragraph break in an older announcement would flatten into one run-on
    // paragraph, and the next Save would persist that flattening — the announcement
    // comes back visibly worse from an edit that never touched the body.
    const seededBody = isRichTextBody(announcement.body) ? announcement.body : plainTextBodyToHtml(announcement.body)
    const seededRoleNames = announcement.requiredRoleNames ?? []
    const seededLegacyPermission = announcement.requiredPermission ?? null
    const seededLinkedIds = new Set(announcement.boardItems.map(i => i.boardItemId))

    setTitle(announcement.title)
    setBody(seededBody)
    setKind(announcement.kind)
    setCtaLabel(announcement.ctaLabel ?? '')
    setCtaRoute(announcement.ctaRoute ?? '')
    setTourKey(announcement.tourKey ?? '')
    setRoleNames(seededRoleNames)
    setLegacyPermission(seededLegacyPermission)
    setLinkedIds(seededLinkedIds)

    // The isDirty baseline moves with the load, not just the visible fields —
    // otherwise Save would read "dirty" the instant the record arrived.
    initialValuesRef.current = {
      title: announcement.title,
      body: seededBody,
      kind: announcement.kind,
      ctaLabel: announcement.ctaLabel ?? '',
      ctaRoute: announcement.ctaRoute ?? '',
      tourKey: announcement.tourKey ?? '',
      roleNames: seededRoleNames,
      legacyPermission: seededLegacyPermission,
      linkedIds: seededLinkedIds,
    }
  }, [announcement?.id])

  const isDirty = useMemo(() => {
    const initial = initialValuesRef.current
    return (
      title !== initial.title ||
      body !== initial.body ||
      kind !== initial.kind ||
      ctaLabel !== initial.ctaLabel ||
      ctaRoute !== initial.ctaRoute ||
      tourKey !== initial.tourKey ||
      legacyPermission !== initial.legacyPermission ||
      roleNames.length !== initial.roleNames.length ||
      roleNames.some(name => !initial.roleNames.includes(name)) ||
      linkedIds.size !== initial.linkedIds.size ||
      [...linkedIds].some(itemId => !initial.linkedIds.has(itemId))
    )
  }, [title, body, kind, ctaLabel, ctaRoute, tourKey, roleNames, legacyPermission, linkedIds])

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase()
    if (!q) return boardItems
    return boardItems.filter(
      i =>
        i.title.toLowerCase().includes(q) ||
        (i.companyName ?? '').toLowerCase().includes(q),
    )
  }, [boardItems, itemSearch])

  const toggleItem = (itemId: string) => {
    setLinkedIds(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  // Mirrors the API's own rule so the staffer is told before the round trip, not by a
  // 400 afterwards. The server still enforces it — this is the convenience copy, and
  // the server is the boundary.
  const ctaIncomplete =
    (Boolean(ctaLabel.trim()) && !ctaRoute.trim() && !tourKey.trim()) ||
    (!ctaLabel.trim() && Boolean(ctaRoute.trim() || tourKey.trim()))
  const routeInvalid = Boolean(ctaRoute.trim()) && !ctaRoute.trim().startsWith('/')
  // §"disable Save on field error": the submit is dead while any of its own fields is
  // invalid, rather than letting the click fail.
  // `body.trim()` was the check and is wrong now the body is rich text: an EMPTY tiptap
  // document serialises to `<p></p>`, four characters of non-whitespace, so a required
  // check against the raw string passes on a completely empty body and Save goes live
  // over nothing. `announcementBodyHasText` strips the markup and asks the real question.
  const canSave =
    Boolean(title.trim()) && announcementBodyHasText(body) && !ctaIncomplete && !routeInvalid

  const close = () => navigate(ROUTES.INTERNAL_ANNOUNCEMENTS)

  const submit = async () => {
    setError(null)
    const payload = {
      title: title.trim(),
      // NOT trimmed to empty-check here — `canSave` already did that against the text
      // rather than the markup. The trim is only to drop the whitespace tiptap leaves
      // around its serialised document.
      body: body.trim(),
      kind,
      // Empty string means "cleared", which the API reads as null. Distinct from
      // `undefined` ("not supplied"), and the difference is what stops an edit
      // wiping a field the form did not render.
      ctaLabel: ctaLabel.trim() || null,
      ctaRoute: ctaRoute.trim() || null,
      tourKey: tourKey.trim() || null,
      // Sent on EVERY save, and only ever as the value now on screen. On an untouched
      // row that is the same string it already held, so a save is a no-op; once the
      // staffer clears it, this is what actually retires it. Sending it always rather
      // than conditionally is what keeps "what I see is what is stored" true — the
      // alternative (omit unless cleared) makes the field's absence carry meaning, and
      // that is the shape of bug the API's undefined-vs-null split exists to avoid.
      requiredPermission: legacyPermission,
      requiredRoleNames: roleNames.length > 0 ? roleNames : null,
      boardItemIds: [...linkedIds],
    }

    try {
      if (isEdit && id) {
        await update.mutateAsync({ id, ...payload })
      } else {
        await create.mutateAsync(payload)
      }
      close()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const saving = create.isPending || update.isPending
  const pageTitle = isEdit ? 'Edit announcement' : 'New announcement'

  /**
   * The 403 this surface ships in until the role grant lands, and the 404 for a stale
   * edit link, both land here rather than rendering an empty form. An empty form over a
   * failed read is the `data = []` lie in form clothing: it looks like a blank record,
   * and the first Save would write those blanks over a real one.
   */
  if (isEdit && isError) {
    return (
      <Column>
        <Header title={pageTitle} breadcrumbsOveride={pageTitle} />
        <QueryErrorState
          error={loadError}
          subject="that announcement"
          permissionLabel="Manage what's new"
          onRetry={() => refetch()}
        />
      </Column>
    )
  }

  if (isEdit && loadingAnnouncement) {
    return <InternalAnnouncementEditPageSkeleton />
  }

  return (
    <Column>
      <Header
        title={pageTitle}
        // The trail's last segment — the uuid on edit, "new" on create — is replaced by
        // this, so the crumb reads "Announcements / Edit announcement". The parent crumb
        // is the real link back (§42); there is deliberately no back BUTTON.
        breadcrumbsOveride={pageTitle}
        rightComponents={
          // One primary, rightmost (§26/§33). Cancel is the secondary beside it.
          // Cancel stays enabled always — it's this page's exit affordance
          // (there is no back BUTTON, see the comment above), not a discard.
          <>
            <Button variant="secondary" onClick={close} disabled={saving}>
              Cancel
            </Button>
            {/* Disabled until dirty in edit mode (VRG-364) — `create` mirrors
                ProductEditPage's `page === 'edit'` split: a fresh draft starts
                empty and has nothing to be dirty against, so `canSave` (the
                field-completeness check above) is the only gate there. This
                form keeps its values in `useState`, not react-hook-form, so
                `isDirty` here is a manual comparison against the loaded
                record rather than `formState.isDirty` — see the ref above. */}
            <Button
              variant="primary"
              onClick={submit}
              disabled={!canSave || saving || (isEdit && !isDirty)}
              loading={saving}
            >
              {isEdit ? 'Save' : 'Create draft'}
            </Button>
          </>
        }
        // Stated up front rather than discovered after clicking Create: creating never
        // publishes. Under the title as a page description, not at the foot of the form
        // (§40) — a caveat read after the staffer has already acted cannot inform them.
        description={
          isEdit
            ? undefined
            : 'This is saved as a draft. Nothing is shown to any brand until you publish it.'
        }
      />

      {/* No second `maxWidth` here. The `Column` above already sets the measure, and a
          narrower inner cap would put the header at 1100 and the cards at 760 — the
          exact title-adrift-from-content misalignment wrapping the header was for. */}
      <Stack spacing={4}>
        <Card title="Announcement" spacing={4}>
          <Stack spacing={4}>
            <TextField
              {...formMethods}
              name="title"
              label="Title"
              value={title}
              placeholder="Bulk-export certificates as CSV"
              onValueChange={setTitle}
            />

            {/* RICH TEXT (VRG-332), using the existing `RichTextEditorField` rather
                than a second editor library — it is already aligned to the field chrome
                §31 defines (the `primary[100]` rest border, the `brand[600]` hover and
                focus, the brand focus ring), so the body matches the fields above and
                below it without restyling anything.

                What it replaces was a `TextField` with `inputProps={{multiline: true,
                rows: 6}}`, which never worked: the platform `TextField` does not forward
                those to MUI's `Input`, so it rendered a ONE-LINE `<input>` (measured 23px
                against the six rows it asked for) and a staffer wrote a multi-paragraph
                announcement through a single-line box.

                The rendered body is sanitised against a tag allow-list (see
                `utils/announcementBody`), because this text lands in every brand's
                console — that is a render-side guarantee, not a promise about this
                form. */}
            <RichTextEditorField
              label="Body"
              value={body}
              onChange={setBody}
              minHeight="220px"
              placeholder="What shipped, and why it matters."
            />

            <SelectField
              label="Kind"
              options={KIND_OPTIONS}
              value={kind}
              onChange={v => setKind(v as AnnouncementKind)}
            />
          </Stack>
        </Card>

        <Card title="Audience" spacing={4}>
          {/* MULTI-select of roles, reusing the shared `SelectField`'s `multiple` mode
              (right-aligned checks) rather than a hand-rolled checklist — §28: grep for
              the shared control before building one. The `AssignUsersModal` search +
              checklist pattern is the other candidate and is the wrong fit here: it
              exists for picking from an unbounded, searchable set of RECORDS, and this
              is ten fixed options that all fit on screen at once.

              `placeholder` carries the default state's meaning. Nothing selected means
              EVERYONE, and an empty select renders as a BLANK box — measured, not
              assumed: the Kind select beside it reads "Feature" while this one had no
              text at all. Blank is the worst possible default for the field that decides
              who can see the announcement, because it reads as "unset" when it actually
              means "no gate — every brand sees this". */}
          <SelectField
            label="Roles"
            placeholder="Everyone"
            multiple
            options={ROLE_OPTIONS}
            value={roleNames}
            onChange={v => setRoleNames(Array.isArray(v) ? v : [v])}
          />
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, mt: 2 }}>
            {roleNames.length === 0
              ? 'Everyone sees this announcement. Pick roles to narrow it, so you never announce something the reader cannot open.'
              : 'Only people in these roles see the announcement. Roles are matched by name, so a company that uses its own custom roles instead of the built-in ones will not match.'}
          </Typography>

          {/* The LEGACY permission gate. Shown, not hidden, and read-only.

              This row was authored before the gate targeted roles, and the API still
              enforces the permission — so an editor that simply did not mention it would
              be showing "Everyone" over an announcement that is in fact hidden from most
              readers. Retiring it is a human decision (translating a permission into an
              equivalent role set can only widen or narrow the audience wrongly; see the
              service), so the form states the situation and offers the one safe action.

              `X`, not a trash can: this removes a gate, it does not destroy a record
              (§38). No confirmation for the same reason — it is cheap to undo by
              cancelling out of the form. */}
          {legacyPermission && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={3}
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body3" component="div" sx={{ fontWeight: 600, mb: 1 }}>
                  Also gated on the "{legacyPermission}" permission
                </Typography>
                <Typography variant="body3" component="div" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  This was set before audiences targeted roles, and it still applies on top
                  of any roles above. Remove it if the roles now describe who should see
                  this. Leaving it in place is safe. It only ever narrows the audience.
                </Typography>
              </Box>
              <Button
                variant="secondary"
                onClick={() => setLegacyPermission(null)}
              >
                Remove
              </Button>
            </Stack>
          )}
        </Card>

        {/* CTA. Label + destination, where the destination may be a route, a tour, or
            both — a tour is the strong link between an announcement and the guided
            walkthrough of what just shipped. */}
        <Card title="Call to action" spacing={4}>
          <Stack spacing={4}>
            <TextField
              {...formMethods}
              name="ctaLabel"
              label="Button label (optional)"
              value={ctaLabel}
              placeholder="Try it"
              onValueChange={setCtaLabel}
            />
            <TextField
              {...formMethods}
              name="ctaRoute"
              label="Button goes to (in-app path)"
              value={ctaRoute}
              placeholder="/certificates"
              onValueChange={setCtaRoute}
              helperText={routeInvalid ? 'Must be an in-app path starting with "/".' : undefined}
            />
            <TextField
              {...formMethods}
              name="tourKey"
              label="…and/or starts this tour"
              value={tourKey}
              placeholder="certificates-basics"
              onValueChange={setTourKey}
            />
            {ctaIncomplete && (
              <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.5 }}>
                A button needs both a label and somewhere to go. Set a path, a tour, or
                both. Or clear all three.
              </Typography>
            )}
          </Stack>
        </Card>

        {/* Linked feature requests — search + multi-select checklist, copied from the
            AssignUsersModal pattern (§28) rather than a bare MUI multi-select. */}
        <Card title={`Closes these feature requests (${linkedIds.size} selected)`} spacing={4}>
          <Typography variant="body3" component="div" color="text.secondary" sx={{ lineHeight: 1.5, mb: 3 }}>
            Linking is what makes "Notify requesters" work. Everyone who voted for or
            submitted these is who gets told when you run it.
          </Typography>
          <Search
            value={itemSearch}
            onChange={setItemSearch}
            placeholder="Search feature requests"
          />
          <Box
            sx={{
              mt: 3,
              maxHeight: 320,
              overflowY: 'auto',
              border: `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
            }}
          >
            {itemsLoading ? (
              <Typography variant="body3" color="text.secondary" sx={{ p: 3 }}>
                Loading feature requests…
              </Typography>
            ) : filteredItems.length === 0 ? (
              <Typography variant="body3" color="text.secondary" sx={{ p: 3 }}>
                No feature requests match.
              </Typography>
            ) : (
              filteredItems.map(item => (
                <Box
                  key={item.boardItemId}
                  onClick={() => toggleItem(item.boardItemId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    // Tint on hover, no border change — the selectable-card pattern
                    // (§34). A brand border here would collide with the selected state.
                    '&:hover': { bgcolor: alpha(theme.palette.brand[500], 0.06) },
                    ...(linkedIds.has(item.boardItemId)
                      ? { bgcolor: alpha(theme.palette.brand[500], 0.1) }
                      : {}),
                  }}
                >
                  <Checkbox checked={linkedIds.has(item.boardItemId)} size="small" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* `component="div"` is load-bearing, not decoration. The theme
                        defines no `variantMapping` for body3/body4, so MUI falls back to
                        <span> — display:inline. These two then render on ONE line with a
                        0px gap (measured), title running straight into "Louis Vuitton ·
                        shipped", and any vertical margin is computed but discarded,
                        because vertical margins do not apply to inline boxes. */}
                    <Typography variant="body3" component="div" noWrap sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body3" component="div" color="text.secondary" noWrap>
                      {item.companyName ?? 'No company'} · {item.status} · {item.voteCount}{' '}
                      {item.voteCount === 1 ? 'vote' : 'votes'}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Card>

        {error && (
          <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.5 }}>
            {error}
          </Typography>
        )}
      </Stack>
    </Column>
  )
}
